import React, { useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  createMuiTheme,
  MuiThemeProvider,
} from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";
import intl from "react-intl-universal";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  Legend,
} from "recharts";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import SpinLoading from "components/Loading/SpinLoading.js";
import MaterialTable from "@material-table/core";

import { useRootSelector, useRootDispatch } from "hooks/reduxHooks";
import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import Reading from "spectral-core/models/Reading";
import TextField from "@material-ui/core/TextField";
import { getModels } from "spectral-core/api";
import { setApplicationsAction } from "store/applications/applications.actions";
import {
  aggregateNutroScanOverview,
  averageParameterByMonth,
  averageParameterByGrowthStage,
  averageParameterByPlantThird,
  averageParameterByWashed,
  averageParameterByCollectDelta,
  averageParameterValue,
  averageReferenceValue,
  averageReferenceByMonth,
  averageReferenceByGrowthStage,
  averageReferenceByPlantThird,
  averageReferenceByWashed,
  averageReferenceByCollectDelta,
  buildModelReferenceMap,
  heatmapCounts,
  nirVsLabChartData,
  parameterNamesUnion,
  readingsToMapPins,
  filterNutroScanReadings,
  aggregateReport,
  sortByGrowthStageOrder,
  sortByPlantPartOrder,
  MACRO_NUTRIENTS,
  MICRO_NUTRIENTS,
  paramsForCulture,
  countReadingsWithLabResult,
} from "utils/nutroScanAggregates";
import { cultureTypeLabelPt, plantThirdLabelPt } from "spectral-core/cultureEnum";
import { exportToExcel, exportMultiSheetExcel } from "utils/excelExport";
import { getDiretoria } from "utils/diretoriaMap";

//@ts-ignore
import { Map, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "components/Map/map-style.css";

/* ─── Paleta de cores ─── */
const COLORS = {
  primary: "#00838f",
  primaryLight: "#4fb3bf",
  info: "#1565c0",
  infoLight: "#5e92f3",
  success: "#2e7d32",
  successLight: "#60ad5e",
  warning: "#e65100",
  warningLight: "#ff833a",
  nir: "#00838f",
  lab: "#ff8f00",
};

const CHART_BAR_COLORS = [
  "#00838f", "#26a69a", "#43a047", "#66bb6a",
  "#8bc34a", "#cddc39", "#ffb300", "#fb8c00",
  "#ef5350", "#ab47bc", "#5c6bc0", "#29b6f6",
];

//@ts-ignore
const useStyles = makeStyles((theme) => ({
  cardMetric: {
    padding: theme.spacing(2),
    textAlign: "center" as const,
    borderLeft: "4px solid #00838f",
    height: 110,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
  },
  cardMetricMain: {
    padding: theme.spacing(2),
    textAlign: "center" as const,
    borderLeft: "4px solid #00838f",
    backgroundColor: "#e0f7fa",
    height: 110,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  chartContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#37474f",
    marginBottom: "8px",
  },
}));

function TabPanel(props: {
  children?: React.ReactNode;
  index: number;
  value: number;
}) {
  const { children, value, index } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`nutroscan-tabpanel-${index}`}>
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

const tableTheme = createMuiTheme({
  palette: {
    primary: { main: "#00838f" },
    secondary: { main: "#00838f" },
  },
});

/* ─── Componente de gráfico horizontal ─── */
function HBarChart({
  title,
  data,
  color,
  sortDesc = false,
}: {
  title: string;
  data: { name: string; value: number }[];
  color: string;
  sortDesc?: boolean;
}) {
  const classes = useStyles();
  const sorted = sortDesc
    ? [...data].sort((a, b) => b.value - a.value)
    : data;

  if (!sorted.length) {
    return (
      <Paper className={classes.chartContainer}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
          Sem dados
        </Typography>
      </Paper>
    );
  }

  const height = Math.max(250, sorted.length * 38 + 40);
  const maxLabelLen = Math.max(...sorted.map((d) => d.name.length));
  const yWidth = Math.min(Math.max(maxLabelLen * 7, 70), 200);

  return (
    <Paper className={classes.chartContainer}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis
            type="category"
            dataKey="name"
            width={yWidth}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(val: any) => [val, "Quantidade"]}
            contentStyle={{ borderRadius: 8 }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
            {sorted.map((_entry, idx) => (
              <Cell key={idx} fill={CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length]} />
            ))}
            <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 500 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

/* ─── Componente de gráfico vertical ─── */
function VBarChart({
  title,
  data,
  color,
  valueLabel,
  formatValue,
}: {
  title: string;
  data: { name: string; value: number }[];
  color: string;
  valueLabel?: string;
  formatValue?: (v: number) => string;
}) {
  const classes = useStyles();

  if (!data.length) {
    return (
      <Paper className={classes.chartContainer}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
          Sem dados
        </Typography>
      </Paper>
    );
  }

  const height = Math.max(300, data.length * 40 + 80);
  const fmt = formatValue || ((v: number) => v.toFixed(2));

  return (
    <Paper className={classes.chartContainer}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 15, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} height={60} interval={0} angle={-30} textAnchor="end" />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            formatter={(val: any) => [fmt(Number(val)), valueLabel || "Valor"]}
            contentStyle={{ borderRadius: 8 }}
          />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} maxBarSize={40}>
            <LabelList
              dataKey="value"
              position="top"
              style={{ fontSize: 11, fontWeight: 500 }}
              formatter={(v: any) => fmt(Number(v))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

/* ─── Gráfico de linhas duplo (NIR + Lab) para correlações ─── */
function DualLineChart({
  title,
  nirData,
  labData,
  unitShort,
  unitLabel,
  classes,
  emptyMsg,
}: {
  title: string;
  nirData: { label: string; value: number }[];
  labData: { label: string; value: number }[];
  unitShort?: string;
  unitLabel?: string;
  classes: any;
  emptyMsg?: string;
}) {
  const labels = Array.from(new Set([...nirData.map((d) => d.label), ...labData.map((d) => d.label)]));
  const merged = labels.map((label) => {
    const nirItem = nirData.find((d) => d.label === label);
    const labItem = labData.find((d) => d.label === label);
    return {
      name: label,
      NIR: nirItem ? Number(nirItem.value.toFixed(4)) : null,
      Lab: labItem ? Number(labItem.value.toFixed(4)) : null,
    };
  });

  const hasAny = merged.some((m) => m.NIR != null || m.Lab != null);
  if (!merged.length || !hasAny) {
    return (
      <Paper className={classes.chartContainer}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
          {emptyMsg || "Sem dados para o período e filtros selecionados."}
        </Typography>
      </Paper>
    );
  }

  const height = 340;

  return (
    <Paper className={classes.chartContainer}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={merged} margin={{ top: 15, right: 25, bottom: 5, left: 30 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} height={60} interval={0} angle={-30} textAnchor="end" />
          <YAxis
            tick={{ fontSize: 12 }}
            label={unitLabel ? { value: unitLabel, angle: -90, position: "insideLeft", style: { textAnchor: "middle" }, offset: -10 } : undefined}
          />
          <Tooltip
            formatter={(val: any) => [val == null ? "—" : Number(val).toFixed(2) + (unitShort ? " " + unitShort : ""), ""]}
            contentStyle={{ borderRadius: 8 }}
          />
          <Legend />
          <Line type="monotone" dataKey="NIR" name="NutroScan" stroke={COLORS.nir} strokeWidth={2} dot={{ r: 4 }} connectNulls />
          <Line type="monotone" dataKey="Lab" name="Laboratório" stroke={COLORS.lab} strokeWidth={2} dot={{ r: 4 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}

export default function NutroScan() {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [mapNutrient, setMapNutrient] = useState<string>("");
  const [mapSource, setMapSource] = useState<"nir" | "lab">("nir");
  const [corrNutrient, setCorrNutrient] = useState<string>("");
  const [filterDiretoria, setFilterDiretoria] = useState<string>("");
  const [filterCulture, setFilterCulture] = useState<string>("");
  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [filterName, setFilterName] = useState<string[]>([]);
  const [filterSampleCode, setFilterSampleCode] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");

  const isLoggedIn = useUserLoggedIn();
  useGetReadings();
  const rawReadings = useRootSelector((state) => state.reading.readings) as
    | Reading[]
    | undefined;

  // Modelos NIR cadastrados (carrega Min/Max/Unit por nutriente — mesma fonte usada
  // pelo app movel iSpectral2 e pelo PDF de relatorio do backend).
  const dispatch = useRootDispatch();
  const accessToken = useRootSelector(
    (state) => state.user.currentToken?.accessToken
  );
  const spectralModels = useRootSelector(
    (state) => state.applications.applications
  );
  useEffect(() => {
    let cancelled = false;
    async function loadModels() {
      if (!isLoggedIn || !accessToken) return;
      if (spectralModels !== undefined) return;
      try {
        const apiModels = await getModels(accessToken);
        if (!cancelled) dispatch(setApplicationsAction(apiModels));
      } catch {
        /* silencia — Mapa cai em "Indefinido" (cinza) se modelos nao carregarem */
      }
    }
    loadModels();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, accessToken, spectralModels, dispatch]);

  const modelReferenceMap = useMemo(
    () => buildModelReferenceMap(spectralModels),
    [spectralModels]
  );

  const loading = !isLoggedIn || rawReadings === undefined;

  // Filtro global (sem São Carlos, apenas médias)
  const baseReadings = useMemo(
    () => (rawReadings ? filterNutroScanReadings(rawReadings, true) : []),
    [rawReadings]
  );

  // Diretoria: lista todas do dataset base (primeiro filtro)
  const availableDiretorias = useMemo(() => {
    const set = new Set<string>();
    for (const r of baseReadings) {
      const d = getDiretoria(r.userName);
      if (d) set.add(d);
    }
    return Array.from(set).sort();
  }, [baseReadings]);

  // Filtro em cascata: cada lista respeita os filtros anteriores
  const afterDiretoria = useMemo(() => {
    if (!filterDiretoria) return baseReadings;
    return baseReadings.filter((r) => getDiretoria(r.userName) === filterDiretoria);
  }, [baseReadings, filterDiretoria]);

  const availableUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterDiretoria) {
      const user = (r.userName || "").trim();
      if (user) set.add(user);
    }
    return Array.from(set).sort();
  }, [afterDiretoria]);

  const afterUser = useMemo(() => {
    if (filterUser.length === 0) return afterDiretoria;
    return afterDiretoria.filter((r) => filterUser.includes((r.userName || "").trim()));
  }, [afterDiretoria, filterUser]);

  const availableCultures = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterUser) {
      const label = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
      set.add(label);
    }
    return Array.from(set).sort();
  }, [afterUser]);

  const afterCulture = useMemo(() => {
    if (!filterCulture) return afterUser;
    return afterUser.filter(
      (r) => cultureTypeLabelPt(r.culture, r.customCultureName) === filterCulture
    );
  }, [afterUser, filterCulture]);

  const availableNames = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterCulture) {
      const n = (r.name || "").trim();
      if (n) set.add(n);
    }
    return Array.from(set).sort();
  }, [afterCulture]);

  const availableSampleCodes = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterCulture) {
      const sc = (r.sampleCode || "").trim();
      if (sc) set.add(sc);
    }
    return Array.from(set).sort();
  }, [afterCulture]);

  // Filtro por diretoria + usuário + cultura + nome + amostra + data (aplicado pelo usuário)
  const readings = useMemo(() => {
    let filtered = baseReadings;
    if (filterDiretoria) {
      filtered = filtered.filter((r) => getDiretoria(r.userName) === filterDiretoria);
    }
    if (filterUser.length > 0) {
      filtered = filtered.filter(
        (r) => filterUser.includes((r.userName || "").trim())
      );
    }
    if (filterCulture) {
      filtered = filtered.filter(
        (r) => cultureTypeLabelPt(r.culture, r.customCultureName) === filterCulture
      );
    }
    if (filterName.length > 0) {
      filtered = filtered.filter(
        (r) => filterName.includes((r.name || "").trim())
      );
    }
    if (filterSampleCode.length > 0) {
      filtered = filtered.filter(
        (r) => filterSampleCode.includes((r.sampleCode || "").trim())
      );
    }
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return filtered;
  }, [baseReadings, filterDiretoria, filterCulture, filterUser, filterName, filterSampleCode, filterDateFrom, filterDateTo]);

  const overview = useMemo(
    () => (readings.length ? aggregateNutroScanOverview(readings) : null),
    [readings]
  );

  const params = useMemo(
    () => (readings.length ? parameterNamesUnion(readings) : []),
    [readings]
  );

  // Auto-selecionar primeira cultura quando disponível
  React.useEffect(() => {
    if (availableCultures.length && !filterCulture) setFilterCulture(availableCultures[0]);
  }, [availableCultures, filterCulture]);

  React.useEffect(() => {
    if (params.length && !mapNutrient) setMapNutrient(params[0]);
    if (params.length && !corrNutrient) setCorrNutrient(params[0]);
  }, [params, mapNutrient, corrNutrient]);

  const heat = useMemo(
    () => (readings.length ? heatmapCounts(readings, modelReferenceMap) : null),
    [readings, modelReferenceMap]
  );

  const labResultsCount = useMemo(() => 
    (countReadingsWithLabResult(readings)),
  [readings]);

  // --- Correlacoes por dimensao ---
  const corrByMonth = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageParameterByMonth(readings, corrNutrient);
  }, [readings, corrNutrient]);

  const corrByGrowth = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageParameterByGrowthStage(readings, corrNutrient);
  }, [readings, corrNutrient]);

  const corrByPlant = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageParameterByPlantThird(readings, corrNutrient);
  }, [readings, corrNutrient]);

  const corrByWashed = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageParameterByWashed(readings, corrNutrient);
  }, [readings, corrNutrient]);

  const corrByDelta = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageParameterByCollectDelta(readings, corrNutrient);
  }, [readings, corrNutrient]);

  // --- Lab correlations ---
  const corrLabByMonth = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageReferenceByMonth(readings, corrNutrient);
  }, [readings, corrNutrient]);
  const corrLabByGrowth = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageReferenceByGrowthStage(readings, corrNutrient);
  }, [readings, corrNutrient]);
  const corrLabByPlant = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageReferenceByPlantThird(readings, corrNutrient);
  }, [readings, corrNutrient]);
  const corrLabByWashed = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageReferenceByWashed(readings, corrNutrient);
  }, [readings, corrNutrient]);
  const corrLabByDelta = useMemo(() => {
    if (!readings.length || !corrNutrient) return [];
    return averageReferenceByCollectDelta(readings, corrNutrient);
  }, [readings, corrNutrient]);

  // --- Lab comparison ---
  const labRows = useMemo(() => {
    if (!readings.length) return [];
    return params.map((name) => {
      const nir = averageParameterValue(readings, name);
      const lab = averageReferenceValue(readings, name);
      let delta: number | null = null;
      if (nir != null && lab != null && lab !== 0) {
        delta = ((nir - lab) / lab) * 100;
      }
      return { nutrient: name, nir, lab, delta };
    });
  }, [readings, params]);

  const nirLabChart = useMemo(() => {
    if (!readings.length) return null;
    return nirVsLabChartData(readings);
  }, [readings]);

  const mapPins = useMemo(() => {
    if (!readings.length) return [];
    return readingsToMapPins(readings, mapNutrient || null, mapSource, modelReferenceMap);
  }, [readings, mapNutrient, mapSource, modelReferenceMap]);

  // Leituras do Relatório: ignora filtro de cultura (mostra todas)
  const reportReadings = useMemo(() => {
    let filtered = baseReadings;
    if (filterDiretoria) filtered = filtered.filter((r) => getDiretoria(r.userName) === filterDiretoria);
    if (filterUser.length > 0) filtered = filtered.filter((r) => filterUser.includes((r.userName || "").trim()));
    if (filterName.length > 0) filtered = filtered.filter((r) => filterName.includes((r.name || "").trim()));
    if (filterSampleCode.length > 0) filtered = filtered.filter((r) => filterSampleCode.includes((r.sampleCode || "").trim()));
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return filtered;
  }, [baseReadings, filterDiretoria, filterUser, filterName, filterSampleCode, filterDateFrom, filterDateTo]);

  // --- Relatório por CDM/mês/cultura (sempre todas as culturas) ---
  const reportRows = useMemo(
    () => aggregateReport(reportReadings),
    [reportReadings]
  );

  // Unidade do nutriente selecionado em Correlações
  const corrUnitShort = MACRO_NUTRIENTS.includes(corrNutrient)
    ? "g/kg"
    : MICRO_NUTRIENTS.includes(corrNutrient)
    ? "mg/kg"
    : "";
  const corrUnitLabel = corrUnitShort ? `Concentração (${corrUnitShort})` : "";

  if (loading) {
    return <SpinLoading />;
  }

  // --- Overview chart data ---
  const monthData = overview ? Object.keys(overview.byMonth)
    .sort()
    .map((k) => ({ name: k, value: overview.byMonth[k] })) : [];

  const growthData = overview ? sortByGrowthStageOrder(
    Object.keys(overview.byGrowthStage).map((k) => ({
      label: k, name: k, value: overview.byGrowthStage[k],
    }))
  ).map((d) => ({ name: d.label, value: d.value })) : [];

  const plantData = overview ? sortByPlantPartOrder(
    Object.keys(overview.byPlantThird).map((k) => ({
      label: k, name: k, value: overview.byPlantThird[k],
    }))
  ).map((d) => ({ name: d.label, value: d.value })) : [];

  const cultivarData = overview ? overview.topCultivars.map(([k, v]) => ({
    name: k,
    value: v,
  })) : [];

  const washedData = overview ? Object.keys(overview.byWashed).map((k) => ({
    name: k,
    value: overview.byWashed[k],
  })) : [];

  // --- NIR vs Lab chart data (separado macro/micro) ---
  const nirLabMacroData = nirLabChart
    ? nirLabChart
        .filter((x) => MACRO_NUTRIENTS.includes(x.nutrient))
        .map((x) => ({
          name: x.nutrient,
          NIR: x.nir != null ? Number(x.nir.toFixed(4)) : 0,
          Lab: x.lab != null ? Number(x.lab.toFixed(4)) : 0,
        }))
    : [];
  const nirLabMicroData = nirLabChart
    ? nirLabChart
        .filter((x) => MICRO_NUTRIENTS.includes(x.nutrient))
        .map((x) => ({
          name: x.nutrient,
          NIR: x.nir != null ? Number(x.nir.toFixed(4)) : 0,
          Lab: x.lab != null ? Number(x.lab.toFixed(4)) : 0,
        }))
    : [];

  return (
    <MuiThemeProvider theme={tableTheme}>
      <div>
        <GridContainer>
          <GridItem xs={12}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h5" gutterBottom>
                {intl.get("nutroscan.title")}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={() => {
                  if (!readings.length) return;
                  // Exporta dataset completo (todas leituras filtradas)
                  const rows = readings.map((r) => {
                    let lat = "", lng = "";
                    try {
                      if (r.geoLocation && r.geoLocation.length > 5) {
                        const geo = JSON.parse(r.geoLocation);
                        lat = String(geo.Latitude ?? "");
                        lng = String(geo.Longitude ?? "");
                      }
                    } catch { /* skip */ }
                    return {
                      diretoria: getDiretoria(r.userName),
                      usuario: r.userName ?? "",
                      cultura: cultureTypeLabelPt(r.culture, r.customCultureName),
                      cultivar: r.cultivate ?? "",
                      fenologia: r.growthStage ?? "",
                      parte: plantThirdLabelPt(r.plantThird),
                      lavada: r.washed === true ? "Sim" : r.washed === false ? "Não" : "",
                      coleta: r.collectTime ?? "",
                      analise: r.dateTime ?? "",
                      amostra: r.sampleCode ?? "",
                      nome: r.name ?? "",
                      talhao: r.plot ?? "",
                      temperatura: r.temperature ?? "",
                      latitude: lat,
                      longitude: lng,
                    };
                  });
                  exportToExcel(rows, "NutroScan_Leituras", "Leituras", [
                    { header: "Diretoria", field: "diretoria" },
                    { header: "Usuário", field: "usuario" },
                    { header: "Cultura", field: "cultura" },
                    { header: "Cultivar", field: "cultivar" },
                    { header: "Fenologia", field: "fenologia" },
                    { header: "Parte", field: "parte" },
                    { header: "Folha lavada", field: "lavada" },
                    { header: "Coleta", field: "coleta" },
                    { header: "Análise", field: "analise" },
                    { header: "Cód. amostra", field: "amostra" },
                    { header: "Nome", field: "nome" },
                    { header: "Talhão", field: "talhao" },
                    { header: "Temperatura (°C)", field: "temperatura" },
                    { header: "Latitude", field: "latitude" },
                    { header: "Longitude", field: "longitude" },
                  ]);
                }}
              >
                Exportar Excel
              </Button>
            </div>

            {/* Filtros globais — ordem: Diretoria, Usuário, Cultura, Nome, Cód., De, Até */}
            <Paper style={{ padding: "12px 16px", marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <FormControl style={{ minWidth: 140, flex: "1 1 140px" }} size="small">
                <InputLabel id="filter-diretoria" shrink>Diretoria</InputLabel>
                <Select
                  labelId="filter-diretoria"
                  value={filterDiretoria}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFilterDiretoria(e.target.value as string);
                    setFilterUser([]);
                    setFilterCulture("");
                    setFilterName([]);
                    setFilterSampleCode([]);
                  }}
                  displayEmpty
                >
                  <MenuItem value="">Todas</MenuItem>
                  {availableDiretorias.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="filter-user" shrink>Usuário</InputLabel>
                <Select
                  labelId="filter-user"
                  multiple
                  value={filterUser}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFilterUser(e.target.value as string[]);
                    setFilterCulture("");
                    setFilterName([]);
                    setFilterSampleCode([]);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    const sel = selected as string[];
                    if (sel.length === 0) return "Todos";
                    if (sel.length === 1) return sel[0];
                    return `${sel.length} usuários`;
                  }}
                >
                  {availableUsers.map((u) => (
                    <MenuItem key={u} value={u}>
                      <Checkbox checked={filterUser.indexOf(u) > -1} size="small" color="primary" />
                      <ListItemText primary={u} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 140, flex: "1 1 140px" }} size="small">
                <InputLabel id="filter-culture" shrink>Cultura</InputLabel>
                <Select
                  labelId="filter-culture"
                  value={filterCulture}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFilterCulture(e.target.value as string);
                    setFilterName([]);
                    setFilterSampleCode([]);
                  }}
                >
                  {availableCultures.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="filter-name" shrink>Nome da leitura</InputLabel>
                <Select
                  labelId="filter-name"
                  multiple
                  value={filterName}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setFilterName(e.target.value as string[])
                  }
                  displayEmpty
                  renderValue={(selected) => {
                    const sel = selected as string[];
                    if (sel.length === 0) return "Todas";
                    if (sel.length === 1) return sel[0];
                    return `${sel.length} nomes`;
                  }}
                >
                  {availableNames.map((n) => (
                    <MenuItem key={n} value={n}>
                      <Checkbox checked={filterName.indexOf(n) > -1} size="small" color="primary" />
                      <ListItemText primary={n} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="filter-sample" shrink>Cód. amostra</InputLabel>
                <Select
                  labelId="filter-sample"
                  multiple
                  value={filterSampleCode}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setFilterSampleCode(e.target.value as string[])
                  }
                  displayEmpty
                  renderValue={(selected) => {
                    const sel = selected as string[];
                    if (sel.length === 0) return "Todos";
                    if (sel.length === 1) return sel[0];
                    return `${sel.length} códigos`;
                  }}
                >
                  {availableSampleCodes.map((sc) => (
                    <MenuItem key={sc} value={sc}>
                      <Checkbox checked={filterSampleCode.indexOf(sc) > -1} size="small" color="primary" />
                      <ListItemText primary={sc} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="De"
                type="date"
                size="small"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 145, flex: "0 0 145px" }}
              />
              <TextField
                label="Até"
                type="date"
                size="small"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 145, flex: "0 0 145px" }}
              />
              {(filterDiretoria || filterUser.length > 0 || filterName.length > 0 || filterSampleCode.length > 0 || filterDateFrom || filterDateTo) && (
                <Button
                  size="small"
                  onClick={() => {
                    setFilterDiretoria("");
                    setFilterUser([]);
                    setFilterName([]);
                    setFilterSampleCode([]);
                    setFilterDateFrom("");
                    setFilterDateTo("");
                  }}
                >
                  Limpar filtros
                </Button>
              )}
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto", textAlign: "right" }}>
                {readings.length} análises (médias)
              </Typography>
            </Paper>

            <Tabs
              value={tab}
              onChange={(_e: React.ChangeEvent<{}>, v: number) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={intl.get("nutroscan.tab_overview")} />
              <Tab label={intl.get("nutroscan.tab_lab")} />
              <Tab label="Classe nutricional" />
              <Tab label={intl.get("nutroscan.tab_corr")} />
              <Tab label={intl.get("nutroscan.tab_map")} />
              <Tab label="Relatório" />
            </Tabs>

            {/* ===================== TAB 0 — VISAO GERAL ===================== */}
            <TabPanel value={tab} index={0}>
              {!overview ? (
                <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
                  Nenhuma leitura corresponde aos filtros selecionados.
                </Typography>
              ) : (<>
              {/* KPIs */}
              <Grid container spacing={2} style={{ marginBottom: 24 }}>
                <Grid item xs={12} sm={6} md={4} lg={true}>
                  <Paper className={classes.cardMetricMain}>
                    <Typography variant="caption">
                      {intl.get("nutroscan.total_reads")}
                    </Typography>
                    <Typography variant="h4" style={{ fontWeight: 700, color: "#00838f" }}>
                      {overview.totalReadings}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={true}>
                  <Paper className={classes.cardMetric}>
                    <Typography variant="caption">
                      {intl.get("nutroscan.lab_results")}
                    </Typography>
                    <Typography variant="h6">
                      {labResultsCount}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={true}>
                  <Paper className={classes.cardMetric}>
                    <Typography variant="caption">
                      {intl.get("nutroscan.last_sync")}
                    </Typography>
                    <Typography variant="body1">
                      {overview.lastSync
                        ? overview.lastSync.toLocaleString()
                        : "—"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={true}>
                  <Paper className={classes.cardMetric}>
                    <Typography variant="caption">
                      {intl.get("nutroscan.avg_temp")}
                    </Typography>
                    <Typography variant="h6">
                      {overview.avgTemperature != null
                        ? overview.avgTemperature.toFixed(1) + " °C"
                        : "—"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={true}>
                  <Paper className={classes.cardMetric}>
                    <Typography variant="caption">Cultivares distintos</Typography>
                    <Typography variant="h5" style={{ color: "#00838f" }}>
                      {overview.distinctCultivarsCount}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Linha 1: Volume por mês */}
              <GridContainer className={classes.tabPanel}>
                <GridItem xs={12}>
                  <HBarChart
                    title={intl.get("nutroscan.chart_volume_month")}
                    data={monthData}
                    color={COLORS.primary}
                  />
                </GridItem>
              </GridContainer>

              {/* Linha 2: Fenologia + Parte planta */}
              <GridContainer>
                <GridItem xs={12} md={6}>
                  <HBarChart
                    title={intl.get("nutroscan.chart_growth")}
                    data={growthData}
                    color={COLORS.success}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <HBarChart
                    title={intl.get("nutroscan.chart_plant_part")}
                    data={plantData}
                    color={COLORS.warning}
                  />
                </GridItem>
              </GridContainer>

              {/* Linha 3: Top cultivares + Folha lavada */}
              <GridContainer>
                <GridItem xs={12} md={6}>
                  <HBarChart
                    title="Top 10 cultivares"
                    data={cultivarData}
                    color={COLORS.primaryLight}
                    sortDesc
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <HBarChart
                    title="Folha lavada"
                    data={washedData}
                    color={COLORS.successLight}
                    sortDesc
                  />
                </GridItem>
              </GridContainer>
              </>)}
            </TabPanel>

            {/* ===================== TAB 1 — NIR vs LABORATÓRIO (linhas) ===================== */}
            <TabPanel value={tab} index={1}>
              <Typography variant="body2" paragraph color="textSecondary">
                {intl.get("nutroscan.lab_hint")}
              </Typography>

              <GridContainer>
                <GridItem xs={12} md={6}>
                  {nirLabMacroData.length ? (
                    <Paper className={classes.chartContainer}>
                      <Typography className={classes.chartTitle}>Macronutrientes</Typography>
                      <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={nirLabMacroData} margin={{ top: 15, right: 20, bottom: 5, left: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            label={{ value: "Concentração (g/kg)", angle: -90, position: "insideLeft", style: { textAnchor: "middle" }, offset: -10 }}
                          />
                          <Tooltip formatter={(val: any) => [Number(val).toFixed(2) + " g/kg", ""]} contentStyle={{ borderRadius: 8 }} />
                          <Legend />
                          <Line type="monotone" dataKey="NIR" name="NutroScan" stroke={COLORS.nir} strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="Lab" name="Laboratório" stroke={COLORS.lab} strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  ) : null}
                </GridItem>
                <GridItem xs={12} md={6}>
                  {nirLabMicroData.length ? (
                    <Paper className={classes.chartContainer}>
                      <Typography className={classes.chartTitle}>Micronutrientes</Typography>
                      <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={nirLabMicroData} margin={{ top: 15, right: 20, bottom: 5, left: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            label={{ value: "Concentração (mg/kg)", angle: -90, position: "insideLeft", style: { textAnchor: "middle" }, offset: -10 }}
                          />
                          <Tooltip formatter={(val: any) => [Number(val).toFixed(2) + " mg/kg", ""]} contentStyle={{ borderRadius: 8 }} />
                          <Legend />
                          <Line type="monotone" dataKey="NIR" name="NutroScan" stroke={COLORS.nir} strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="Lab" name="Laboratório" stroke={COLORS.lab} strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  ) : null}
                </GridItem>
              </GridContainer>
            </TabPanel>

            {/* ===================== TAB 2 — CLASSE NUTRICIONAL ===================== */}
            <TabPanel value={tab} index={2}>
              <Typography variant="body2" paragraph color="textSecondary">
                Contagem por classificação (±10% em torno da referência). Célula em destaque indica a classe predominante.
              </Typography>
              {heat && heat.nutrients.length ? (() => {
                const expectedParams = paramsForCulture(filterCulture);
                // Ordenar: primeiro os esperados na ordem canônica, depois os extras encontrados
                const filteredNutrients = [
                  ...expectedParams.filter((p) => heat.nutrients.includes(p)),
                ];
                const nirRows = filteredNutrients.map((n) => {
                  const d = heat.grid[n]?.Deficiente ?? 0, a = heat.grid[n]?.Adequado ?? 0, e = heat.grid[n]?.Excesso ?? 0;
                  return { n, d, a, e, max: Math.max(d, a, e) };
                });
                const labRows2 = filteredNutrients.map((n) => {
                  const d = heat.labGrid[n]?.Deficiente ?? 0, a = heat.labGrid[n]?.Adequado ?? 0, e = heat.labGrid[n]?.Excesso ?? 0;
                  return { n, d, a, e, max: Math.max(d, a, e) };
                });
                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<GetAppIcon />}
                        onClick={() =>
                          exportMultiSheetExcel(
                            [
                              {
                                name: "NutroScan",
                                data: nirRows,
                                columns: [
                                  { header: "Nutriente", field: "n" },
                                  { header: "Deficiente", field: "d" },
                                  { header: "Adequado", field: "a" },
                                  { header: "Excesso", field: "e" },
                                ],
                              },
                              {
                                name: "Laboratorio",
                                data: labRows2,
                                columns: [
                                  { header: "Nutriente", field: "n" },
                                  { header: "Deficiente", field: "d" },
                                  { header: "Adequado", field: "a" },
                                  { header: "Excesso", field: "e" },
                                ],
                              },
                            ],
                            "NutroScan_ClasseNutricional"
                          )
                        }
                      >
                        Exportar Excel
                      </Button>
                    </div>
                    <GridContainer>
                      <GridItem xs={12} md={6}>
                        <MaterialTable
                          title="NutroScan — Nutriente vs Classificação"
                          columns={[
                            { title: "Nutriente", field: "n" },
                            { title: "Deficiente", field: "d",
                              headerStyle: { backgroundColor: "#ffcdd2" },
                              render: (row: any) => <span style={{ fontWeight: row.d === row.max ? 700 : 400, backgroundColor: row.d === row.max ? "#ffcdd2" : undefined, padding: "2px 6px", borderRadius: 4, color: "#c62828" }}>{row.d}</span>,
                            },
                            { title: "Adequado", field: "a",
                              headerStyle: { backgroundColor: "#c8e6c9" },
                              render: (row: any) => <span style={{ fontWeight: row.a === row.max ? 700 : 400, backgroundColor: row.a === row.max ? "#c8e6c9" : undefined, padding: "2px 6px", borderRadius: 4, color: "#2e7d32" }}>{row.a}</span>,
                            },
                            { title: "Excesso", field: "e",
                              headerStyle: { backgroundColor: "#ffe0b2" },
                              render: (row: any) => <span style={{ fontWeight: row.e === row.max ? 700 : 400, backgroundColor: row.e === row.max ? "#ffe0b2" : undefined, padding: "2px 6px", borderRadius: 4, color: "#e65100" }}>{row.e}</span>,
                            },
                          ]}
                          data={nirRows}
                          options={{ paging: false, search: false }}
                        />
                      </GridItem>
                      <GridItem xs={12} md={6}>
                        <MaterialTable
                          title="Laboratório — Nutriente vs Classificação"
                          columns={[
                            { title: "Nutriente", field: "n" },
                            { title: "Deficiente", field: "d",
                              headerStyle: { backgroundColor: "#ffcdd2" },
                              render: (row: any) => <span style={{ fontWeight: row.d === row.max ? 700 : 400, backgroundColor: row.d === row.max ? "#ffcdd2" : undefined, padding: "2px 6px", borderRadius: 4, color: "#c62828" }}>{row.d}</span>,
                            },
                            { title: "Adequado", field: "a",
                              headerStyle: { backgroundColor: "#c8e6c9" },
                              render: (row: any) => <span style={{ fontWeight: row.a === row.max ? 700 : 400, backgroundColor: row.a === row.max ? "#c8e6c9" : undefined, padding: "2px 6px", borderRadius: 4, color: "#2e7d32" }}>{row.a}</span>,
                            },
                            { title: "Excesso", field: "e",
                              headerStyle: { backgroundColor: "#ffe0b2" },
                              render: (row: any) => <span style={{ fontWeight: row.e === row.max ? 700 : 400, backgroundColor: row.e === row.max ? "#ffe0b2" : undefined, padding: "2px 6px", borderRadius: 4, color: "#e65100" }}>{row.e}</span>,
                            },
                          ]}
                          data={labRows2}
                          options={{ paging: false, search: false }}
                        />
                      </GridItem>
                    </GridContainer>
                  </>
                );
              })() : (
                <Typography>—</Typography>
              )}
            </TabPanel>

            {/* ===================== TAB 3 — CORRELAÇÕES (NIR + Lab) ===================== */}
            <TabPanel value={tab} index={3}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <FormControl style={{ minWidth: 220 }}>
                  <InputLabel id="corr-param">{intl.get("nutroscan.param")}</InputLabel>
                  <Select
                    labelId="corr-param"
                    value={corrNutrient}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                      setCorrNutrient(e.target.value as string)
                    }
                  >
                    {params.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={() => {
                    const rows = [
                      ...corrByMonth.map((d) => ({ dimensao: "Mês", categoria: d.label, NIR: d.value, Lab: corrLabByMonth.find((x) => x.label === d.label)?.value ?? "" })),
                      ...corrByGrowth.map((d) => ({ dimensao: "Fenologia", categoria: d.label, NIR: d.value, Lab: corrLabByGrowth.find((x) => x.label === d.label)?.value ?? "" })),
                      ...corrByPlant.map((d) => ({ dimensao: "Parte da planta", categoria: d.label, NIR: d.value, Lab: corrLabByPlant.find((x) => x.label === d.label)?.value ?? "" })),
                      ...corrByWashed.map((d) => ({ dimensao: "Folha lavada", categoria: d.label, NIR: d.value, Lab: corrLabByWashed.find((x) => x.label === d.label)?.value ?? "" })),
                      ...corrByDelta.map((d) => ({ dimensao: "Tempo coleta→análise", categoria: d.label, NIR: d.value, Lab: corrLabByDelta.find((x) => x.label === d.label)?.value ?? "" })),
                    ];
                    exportToExcel(rows, `NutroScan_Correlacoes_${corrNutrient}`, "Correlacoes", [
                      { header: "Dimensão", field: "dimensao" },
                      { header: "Categoria", field: "categoria" },
                      { header: `NutroScan (${corrUnitShort})`, field: "NIR" },
                      { header: `Laboratório (${corrUnitShort})`, field: "Lab" },
                    ]);
                  }}
                >
                  Exportar Excel
                </Button>
              </div>

              {/* Correlacao por mes (full width) */}
              <GridContainer>
                <GridItem xs={12}>
                  <DualLineChart
                    title={`${intl.get("nutroscan.corr_month_title")} — ${corrNutrient}`}
                    nirData={corrByMonth}
                    labData={corrLabByMonth}
                    unitShort={corrUnitShort}
                    unitLabel={corrUnitLabel}
                    classes={classes}
                  />
                </GridItem>
              </GridContainer>

              {/* Correlacao por fenologia (full width) */}
              <GridContainer>
                <GridItem xs={12}>
                  <DualLineChart
                    title={`Média por fenologia — ${corrNutrient}`}
                    nirData={corrByGrowth}
                    labData={corrLabByGrowth}
                    unitShort={corrUnitShort}
                    unitLabel={corrUnitLabel}
                    classes={classes}
                  />
                </GridItem>
              </GridContainer>

              {/* Parte da planta + Folha lavada (lado a lado) */}
              <GridContainer>
                <GridItem xs={12} md={6}>
                  <DualLineChart
                    title={`Média por parte da planta — ${corrNutrient}`}
                    nirData={corrByPlant}
                    labData={corrLabByPlant}
                    unitShort={corrUnitShort}
                    unitLabel={corrUnitLabel}
                    classes={classes}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <DualLineChart
                    title={`Média por folha lavada — ${corrNutrient}`}
                    nirData={corrByWashed}
                    labData={corrLabByWashed}
                    unitShort={corrUnitShort}
                    unitLabel={corrUnitLabel}
                    classes={classes}
                  />
                </GridItem>
              </GridContainer>

              {/* Tempo coleta-analise centralizado */}
              <GridContainer justify="center">
                <GridItem xs={12} md={8}>
                  <DualLineChart
                    title={`Média por tempo coleta-análise — ${corrNutrient}`}
                    nirData={corrByDelta}
                    labData={corrLabByDelta}
                    unitShort={corrUnitShort}
                    unitLabel={corrUnitLabel}
                    classes={classes}
                  />
                </GridItem>
              </GridContainer>
            </TabPanel>

            {/* ===================== TAB 4 — MAPA ===================== */}
            <TabPanel value={tab} index={4}>
              <Typography className={classes.chartTitle} style={{ fontSize: 18, marginBottom: 12 }}>
                Distribuição geoespacial da concentração de nutriente
              </Typography>
              <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <FormControl style={{ minWidth: 220 }}>
                  <InputLabel id="map-nut">Nutriente</InputLabel>
                  <Select
                    labelId="map-nut"
                    value={mapNutrient}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                      setMapNutrient(e.target.value as string)
                    }
                  >
                    {params.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl style={{ minWidth: 180 }}>
                  <InputLabel id="map-source">Fonte</InputLabel>
                  <Select
                    labelId="map-source"
                    value={mapSource}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                      setMapSource(e.target.value as "nir" | "lab")
                    }
                  >
                    <MenuItem value="nir">NutroScan</MenuItem>
                    <MenuItem value="lab">Laboratório</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  style={{ marginLeft: "auto" }}
                  onClick={() =>
                    exportToExcel(
                      mapPins.map((p) => ({
                        nome: p.name,
                        lat: p.position[0],
                        lng: p.position[1],
                        detalhe: p.detail,
                      })),
                      `NutroScan_Mapa_${mapNutrient}_${mapSource}`,
                      "Mapa",
                      [
                        { header: "Nome", field: "nome" },
                        { header: "Latitude", field: "lat" },
                        { header: "Longitude", field: "lng" },
                        { header: "Status", field: "detalhe" },
                      ]
                    )
                  }
                >
                  Exportar Excel
                </Button>
              </div>

              {/* Legenda do mapa */}
              <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#43a047", display: "inline-block" }} />
                  Adequado
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#e53935", display: "inline-block" }} />
                  Deficiente
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#fb8c00", display: "inline-block" }} />
                  Excesso
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#9e9e9e", display: "inline-block" }} />
                  Indefinido
                </span>
              </div>
              <Typography variant="caption" style={{ display: "block", marginBottom: 8, color: "#616161", fontStyle: "italic" }}>
                Classificação baseada nos limites Mín/Máx por nutriente cadastrados nos modelos NIR (mesma fonte usada pelo app móvel e pelo PDF de relatório). Nutrientes sem cadastro de faixa aparecem em cinza.
              </Typography>

              <div style={{ height: "60vh", marginTop: 8 }}>
                {mapPins.length ? (
                  <Map
                    center={
                      [mapPins[0].position[0], mapPins[0].position[1]] as [number, number]
                    }
                    zoom={5}
                    style={{ height: "100%" }}
                  >
                    <TileLayer
                      url={`https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_3vnf_2_b4ced25f80bd977161a67037`}
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    />
                    {mapPins.map((pin, i) => (
                      <CircleMarker
                        key={i}
                        center={pin.position}
                        radius={6}
                        fillOpacity={0.65}
                        color={pin.color}
                        fillColor={pin.color}
                      >
                        <LeafletTooltip>
                          <span>{pin.name}</span>
                          <br />
                          <span>{pin.detail}</span>
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}
                  </Map>
                ) : (
                  <Typography>{intl.get("nutroscan.no_geo")}</Typography>
                )}
              </div>
            </TabPanel>

            {/* ===================== TAB 5 — RELATÓRIO ===================== */}
            <TabPanel value={tab} index={5}>
              <Typography variant="body2" color="textSecondary" paragraph>
                O Relatório exibe todas as culturas, independentemente do filtro de cultura selecionado acima.
              </Typography>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={() =>
                    reportRows.length &&
                    exportToExcel(reportRows, "NutroScan_Relatorio", "Relatorio", [
                      { header: "Diretoria", field: "diretoria" },
                      { header: "Nome CDM", field: "userName" },
                      { header: "Mês", field: "month" },
                      { header: "Última sincronização", field: "lastSync" },
                      { header: "Soja", field: "soja" },
                      { header: "Milho", field: "milho" },
                      { header: "Café", field: "cafe" },
                      { header: "Cana-de-açúcar", field: "cana" },
                      { header: "Algodão", field: "algodao" },
                      { header: "Outras Culturas", field: "outras" },
                      { header: "Demonstração", field: "demonstracao" },
                      { header: "Total", field: "total" },
                    ])
                  }
                >
                  Exportar Excel
                </Button>
              </div>
              <MaterialTable
                title="Relatório por CDM / Mês / Cultura (todas as culturas)"
                columns={[
                  { title: "Diretoria", field: "diretoria" },
                  { title: "Nome CDM", field: "userName" },
                  { title: "Mês", field: "month" },
                  { title: "Última sincronização", field: "lastSync" },
                  { title: "Soja", field: "soja", type: "numeric" },
                  { title: "Milho", field: "milho", type: "numeric" },
                  { title: "Café", field: "cafe", type: "numeric" },
                  { title: "Cana-de-açúcar", field: "cana", type: "numeric" },
                  { title: "Algodão", field: "algodao", type: "numeric" },
                  { title: "Outras Culturas", field: "outras", type: "numeric" },
                  { title: "Demonstração", field: "demonstracao", type: "numeric" },
                  { title: "Total", field: "total", type: "numeric" },
                ]}
                data={reportRows}
                options={{
                  pageSize: 10,
                  pageSizeOptions: [10, 25, 50],
                  filtering: true,
                }}
              />
            </TabPanel>
          </GridItem>
        </GridContainer>
      </div>
    </MuiThemeProvider>
  );
}
